import React from 'react';
import ReactDOM from 'react-dom';
import './BookingLoader.css';

// Lock icon for secure payment
const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// Shield icon for security
const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

// Check icon
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

// Stripe logo
const StripeLogo = () => (
  <svg viewBox="0 0 60 25" fill="currentColor">
    <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a10.3 10.3 0 0 1-4.56.95c-4.01 0-6.83-2.5-6.83-7.28 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.28 0 .76-.04 1.26-.06 1.65zm-4.65-5.6c-.98 0-2.03.76-2.12 2.67h4.26c0-1.26-.64-2.67-2.14-2.67zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-5.13L32.37 0v3.77l-4.13.88V.44zm-4.32 9.35v10.22H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.45-3.32.43zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54-3.15 0-4.37-1.58-4.37-4.51V3.36L15.35 2.5v2.82h3.12v3.37h-3.12v5.82zm-8.16-4.2c-1.97-.5-2.56-.86-2.56-1.48 0-.65.57-1.03 1.64-1.03 1.6 0 3.28.53 4.44 1.15l.35-3.17C9.4 5.2 7.89 4.9 6.25 4.9c-3.2 0-5.46 1.64-5.46 4.42 0 2.22 1.63 3.57 4.54 4.3 2.08.52 2.63.93 2.63 1.58 0 .74-.64 1.11-1.85 1.11-1.77 0-3.78-.73-5.12-1.54l-.35 3.37c1.18.62 3.17 1.17 5.17 1.17 3.43 0 5.76-1.58 5.76-4.5 0-2.47-1.54-3.65-4.26-4.32z" />
  </svg>
);

const LOADER_STATES = {
  PREPARING: 'preparing',
  CREATING: 'creating',
  PAYMENT: 'payment',
  REDIRECTING: 'redirecting',
  SUCCESS: 'success'
};

const BookingLoader = ({
  isVisible,
  state = LOADER_STATES.PREPARING,
  serviceName = 'your appointment',
  // chargeNow = a deposit is being taken (money changes hands). When false this
  // is a card-capture-only booking — card held to secure the slot, £0 today — so
  // we avoid payment language that makes clients think they've paid.
  chargeNow = true
}) => {
  if (!isVisible) return null;

  const getContent = () => {
    switch (state) {
      case LOADER_STATES.PREPARING:
        return {
          title: 'Preparing your booking',
          subtitle: 'Just a moment while we set everything up...',
          showSpinner: true,
          showSecure: false,
          showPaymentProvider: false
        };
      
      case LOADER_STATES.CREATING:
        return {
          title: 'Creating your booking',
          subtitle: 'Securing your time slot...',
          showSpinner: true,
          showSecure: false,
          showPaymentProvider: false
        };
      
      case LOADER_STATES.PAYMENT:
        return {
          title: chargeNow ? 'Setting up secure payment' : 'Securing your booking',
          subtitle: chargeNow
            ? 'Connecting to our payment provider...'
            : 'No payment today. We just need a card to hold your spot...',
          showSpinner: true,
          showSecure: true,
          showPaymentProvider: true
        };

      case LOADER_STATES.REDIRECTING:
        return {
          title: chargeNow ? 'Redirecting to payment' : 'Securing your booking',
          subtitle: chargeNow
            ? 'You\'ll be taken to our secure payment page to complete your booking.'
            : 'You\'ll add a card to hold your appointment. You won\'t be charged now.',
          showSpinner: false,
          showLock: true,
          showSecure: true,
          showPaymentProvider: true
        };
      
      case LOADER_STATES.SUCCESS:
        return {
          title: 'Booking confirmed!',
          subtitle: 'Redirecting you to your confirmation...',
          showSpinner: true,
          showSecure: false,
          showPaymentProvider: false
        };
      
      default:
        return {
          title: 'Processing',
          subtitle: 'Please wait...',
          showSpinner: true,
          showSecure: false,
          showPaymentProvider: false
        };
    }
  };

  const content = getContent();

  // Portal to document.body so the overlay escapes any ancestor with a
  // transform/will-change/filter. Without this, position: fixed would size
  // relative to those ancestors instead of the viewport, pushing the content
  // off-screen on mobile.
  return ReactDOM.createPortal(
    <div className="booking-loader-overlay">
      <div className="booking-loader-content">
        {content.showSpinner && (
          <div className="booking-loader-spinner" />
        )}

        {content.showLock && (
          <div className="booking-loader-icon">
            <LockIcon />
          </div>
        )}

        <h2 className="booking-loader-title">{content.title}</h2>
        <p className="booking-loader-subtitle">{content.subtitle}</p>

        <div className="booking-loader-dots">
          <div className="booking-loader-dot" />
          <div className="booking-loader-dot" />
          <div className="booking-loader-dot" />
        </div>

        {content.showSecure && (
          <div className="booking-loader-secure">
            <ShieldIcon />
            <span>256-bit SSL encrypted</span>
          </div>
        )}

        {content.showPaymentProvider && (
          <div className="booking-loader-provider">
            <span>Powered by</span>
            <StripeLogo />
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export { BookingLoader, LOADER_STATES };
export default BookingLoader;
